<template>
  <q-card flat bordered>
    <div id="intercom-header" class="q-pa-md flex justify-between">
      <div>
        <div class="text-h6">Average Rents by Bed</div>
        <div style="font-size: 12px">
          Quickly compare asking and effective rents by bed within your market.
        </div>
      </div>
      <div class="flex q-gutter-sm">
        <q-select
          v-model="metricSelection"
          style="width: 150px"
          dense
          outlined
          label="Metric"
          :options="metricOptions"
          @update:model-value="
            (ut) => $tracker.changedFilter('metric', 'market_rent_comparison', ut)
          "
        />
      </div>
    </div>
    <q-separator />
    <q-card-section>
      <div style="width: 100%; height: 300px">
        <bar class="chart" :data="rentChartData" :options="chartOptions" :plugins="chartPlugins" />
      </div>
    </q-card-section>
  </q-card>
</template>

<script lang="ts">
import { Bar } from 'vue-chartjs'
import type {
  SimplePropertyDetailsResponse,
  SimpleAvailability,
  SimplePrice,
  SimpleAvailabilityPeriod
} from 'common'
import { type ChartOptions } from 'chart.js'
import { formatMoney, avg } from '@/utils'
import ChartDataLabels from 'chartjs-plugin-datalabels'

export default {
  name: 'MarketReportLatestRents',
  components: {
    Bar
  },
  props: {
    filteredProperties: {
      type: Array<{
        prop: SimplePropertyDetailsResponse
        units: {
          unit: SimpleAvailability
          lastHistory: SimplePrice
          period: SimpleAvailabilityPeriod
        }[]
      }>,
      required: true
    }
  },
  data() {
    return {
      metricSelection: { label: 'Rent', value: 'rent' },
      metricOptions: [
        { label: 'Rent', value: 'rent' },
        { label: 'Rent PSF', value: 'rent_psf' }
      ],
      marketRentsLoading: false,
      chartPlugins: [ChartDataLabels]
    }
  },
  computed: {
    marketRents() {
      const beds: {
        prices: (number | null)[]
        effectivePrices: (number | null)[]
      }[] = []
      for (let i = 0; i < 6; i++) {
        beds.push({ prices: [], effectivePrices: [] })
      }
      for (const { units } of this.filteredProperties) {
        for (const { unit, lastHistory } of units) {
          if (unit.bed !== null) {
            if (this.metricSelection.value === 'rent') {
              beds[unit.bed].prices.push(lastHistory.price)
              beds[unit.bed].effectivePrices.push(lastHistory.effective_price)
            } else if (unit.sqft && lastHistory.price && lastHistory.effective_price) {
              beds[unit.bed].prices.push(lastHistory.price / unit.sqft)
              beds[unit.bed].effectivePrices.push(lastHistory.effective_price / unit.sqft)
            }
          }
        }
      }
      return beds
        .filter((_) => _.prices.length)
        .map((obj, bed) => ({
          bed,
          price: avg(obj.prices),
          effectivePrice: avg(obj.effectivePrices)
        }))
    },
    chartOptions() {
      return {
        responsive: true,
        maintainAspectRatio: false,
        animation: false,
        borderWidth: 1,
        borderRadius: 4,
        barPercentage: 0.9,
        borderSkipped: false,
        plugins: {
          tooltip: {
            callbacks: {
              label: (context) => {
                let label = context.dataset.label || ''
                if (label) {
                  label += ': '
                }
                if (context.parsed.y !== null) {
                  label += formatMoney(
                    context.parsed.y,
                    this.metricSelection.value === 'rent' ? 0 : 2
                  )
                }
                return label
              }
            }
          },
          datalabels: {
            anchor: 'end',
            align: 'top',
            formatter: (val) => formatMoney(val, this.metricSelection.value === 'rent' ? 0 : 2),
            font: { weight: 'bold' }
          }
        },
        scales: {
          y: {
            grace: '25%',
            ticks: {
              callback: (value: number) => {
                return formatMoney(value, this.metricSelection.value === 'rent' ? 0 : 2)
              }
            }
          }
        }
      } as ChartOptions
    },
    rentChartData() {
      return {
        labels: this.marketRents.map((_) => `${_.bed} BR`),
        datasets: [
          {
            label: 'Avg Asking Rent' + (this.metricSelection.value === 'rent_psf' ? ' PSF' : ''),
            backgroundColor: 'rgba(99, 101, 241, 0.5)',
            borderColor: 'rgba(99, 101, 241, 1)',
            data: this.marketRents.map((_) => _.price)
          },
          {
            label: 'Avg Effective Rent' + (this.metricSelection.value === 'rent_psf' ? ' PSF' : ''),
            backgroundColor: 'rgba(255, 126, 0, 0.5)',
            borderColor: 'rgba(255, 126, 0, 1)',
            data: this.marketRents.map((_) => _.effectivePrice)
          }
        ]
      }
    }
  }
}
</script>
